import { Typography } from '@mui/material';

import { CUSTOM_PLAN_DATA } from '@/widgets/pricing/pricingCards/lib/constants';
import { getFormattedPlanPrice } from '@/widgets/pricing/pricingCards/lib/utils/getFormattedPlanPrice';

import { useBalanceStore } from '@/entities/balance/model/useBalanceStore';

import { PaymentInterval, Plan, PlanCategory, Price as PriceType } from '@/shared/types/Billing';

import { PRICING_CARD_DISCOUNT } from '../../constants/pricingCardDiscound';
import { MinutesText } from '../MinutesText';
import { BundleSlider } from './components/BundleSlider';
import { CardButton } from './components/CardButton';
import { SCard, SCardContent, SContainer, SCurrentPlanContainer, SPriceContainer } from './styled';

type PriceBlockProps = {
  bundles: { label: number; value: number }[];
  chosenPlanId: null | string;
  isActiveSubscription: boolean;
  isButtonHidden: boolean;
  isDisabled: boolean;
  isPriceActive: boolean;
  isSliderVisible: boolean;
  minIndexInBundle: number;
  plan: Plan;
  planPrice: PriceType | undefined;
  selectedBundlePlanPosition: number;
  onActionButtonClick: (priceId: PriceType['id'], plan: Plan) => void;
  onChangeBundle: (value: number) => void;
};

export const PriceBlock = ({
  bundles,
  chosenPlanId,
  isActiveSubscription,
  isButtonHidden,
  isDisabled,
  isPriceActive,
  isSliderVisible,
  minIndexInBundle,
  plan,
  planPrice,
  selectedBundlePlanPosition,
  onActionButtonClick,
  onChangeBundle,
}: PriceBlockProps) => {
  const isEnterprise = plan.category === PlanCategory.Enterprise;
  const {
    category,
    metadata: { bundle_value },
  } = plan;
  const { id: priceId, recurring, unit_amount = 0 } = planPrice ?? {};
  const isCustomPlan = plan.id === CUSTOM_PLAN_DATA.id;
  const price = isCustomPlan
    ? 'Custom'
    : `$${getFormattedPlanPrice({ paymentInterval: recurring?.interval, price: unit_amount })}`;

  const discountConfig = PRICING_CARD_DISCOUNT[plan.category as keyof typeof PRICING_CARD_DISCOUNT];
  const showYearDiscount = Boolean(discountConfig && recurring?.interval === PaymentInterval.Year);
  const discountedPrice = showYearDiscount
    ? `$${getFormattedPlanPrice({ paymentInterval: recurring?.interval, price: Math.round(unit_amount * (1 - discountConfig.year)) })}`
    : undefined;

  let priceColor: string | undefined;
  if (isEnterprise) {
    priceColor = 'common.white';
  } else if (showYearDiscount) {
    priceColor = 'grey.300';
  } else {
    priceColor = 'grey.900';
  }

  const { hasSubscriptionCancelAtPeriodEnd } = useBalanceStore();
  const isCurrentPlan = isPriceActive && !hasSubscriptionCancelAtPeriodEnd;

  return (
    <SContainer>
      <SPriceContainer>
        <Typography
          color={priceColor}
          sx={{ textDecoration: showYearDiscount ? 'line-through' : undefined }}
          variant="title-2-sb"
        >
          {price}
        </Typography>
        {showYearDiscount && !isCustomPlan ? <Typography variant="title-2-sb">{discountedPrice}</Typography> : null}
        {!isCustomPlan && (
          <Typography color={isEnterprise ? 'common.white' : 'grey.500'} variant="caption-2-md">
            {recurring?.interval === PaymentInterval.Year ? <span>Billed annually</span> : null}
            <br />
            /per month
          </Typography>
        )}
      </SPriceContainer>
      <SCard
        $isBusiness={category === PlanCategory.Business}
        $isDisabled={isDisabled}
        $isEnterprise={isEnterprise}
        $isOutlined={isPriceActive}
      >
        {isCurrentPlan && (
          <SCurrentPlanContainer>
            <Typography color={isEnterprise ? 'common.white' : 'grey.900'} variant="body-2-md">
              Current plan
            </Typography>
          </SCurrentPlanContainer>
        )}
        {!isButtonHidden && !isCurrentPlan && (
          <CardButton
            chosenPlanId={chosenPlanId}
            data-testid="price-block-action-button-click"
            isActiveSubscription={isActiveSubscription}
            isCustomPlan={isCustomPlan}
            isPriceActive={isPriceActive}
            plan={plan}
            priceId={priceId}
            onClick={onActionButtonClick}
          />
        )}
        <SCardContent>
          <MinutesText category={category} value={bundle_value} />
          <BundleSlider
            disabled={isDisabled}
            disableFromIndex={minIndexInBundle}
            marks={bundles}
            selectedBundlePlanPosition={selectedBundlePlanPosition}
            shouldHide={!isSliderVisible}
            onChange={onChangeBundle}
          />
        </SCardContent>
      </SCard>
    </SContainer>
  );
};
